import { useState } from "react";
import { Phone, Mail, MapPin, Package } from "lucide-react";
import logoImage from "../../assets/images/cst_logo_white.png";

const C = {
  navy:     "#1B2B4B",
  navyDark: "#101D33",
  gold:     "#A8B8C8",
  goldLight:"#FFFFFF",
  white:    "#FFFFFF",
};

const footerColumns = [
  {
    title: "Programs",
    links: ["Class A CDL", "Class B CDL", "Hazmat Endorsement", "Fleet Training", "Box Truck / Delivery"],
    hrefs: ["/programs", "/programs", "/programs", "/programs", "/box-truck"],
  },
  {
    title: "Company",
    links: ["About Us", "Locations", "Career Services", "Veterans Benefits", "Corporate Partners", "Road Ready Certified", "Dispatcher Course"],
    hrefs: ["/about", "/locations", "/career-services", "/veterans", "/sponsor", "/road-ready-certified", "/dispatcher-course"],
  },
  {
    title: "Resources",
    links: ["Blog", "FAQ", "Apply Now", "Employer Portal", "Driver's Lounge"],
    hrefs: ["/blog", "/faq", "/apply", "/employer-portal", "/tribe"],
  },
];

const U = "'Urbanist', sans-serif";

export default function Footer() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:wght@300;400;600;700;800&display=swap');

        .ft-root {
          background: #071628;
          padding: 64px 48px 32px;
          font-family: ${U};
        }
        .ft-inner { max-width: 1200px; margin: 0 auto; }

        /* Main grid: brand col (2fr) + 3 link cols (1fr each) */
        .ft-grid {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr 1fr;
          gap: 48px;
          margin-bottom: 48px;
        }

        /* Bottom bar */
        .ft-bottom {
          border-top: 1px solid rgba(255,255,255,0.08);
          padding-top: 28px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;
        }
        .ft-bottom-links {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }

        /* Link column accordion — mobile only */
        .ft-col-toggle { display: none; }
        .ft-col-body   { display: flex; flex-direction: column; gap: 12px; }

        /* ── Tablet ≤ 1024px ── */
        @media (max-width: 1024px) {
          .ft-root  { padding: 56px 32px 28px; }
          .ft-grid  { grid-template-columns: 1fr 1fr; gap: 36px; }
        }

        /* ── Mobile ≤ 640px ── */
        @media (max-width: 640px) {
          .ft-root  { padding: 48px 20px 24px; }

          /* Stack everything to single column */
          .ft-grid  { grid-template-columns: 1fr; gap: 0; margin-bottom: 32px; }

          /* Brand col gets bottom border */
          .ft-brand { padding-bottom: 28px; border-bottom: 1px solid rgba(255,255,255,0.07); margin-bottom: 4px; }

          /* Each link column becomes an accordion */
          .ft-col {
            border-bottom: 1px solid rgba(255,255,255,0.07);
          }
          .ft-col-toggle {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: space-between;
            background: none;
            border: none;
            cursor: pointer;
            padding: 16px 0;
            font-family: ${U};
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #A8B8C8;
            text-align: left;
          }
          .ft-col-chevron {
            width: 16px; height: 16px;
            display: flex; align-items: center; justify-content: center;
            opacity: 0.5; transition: transform 0.22s ease; flex-shrink: 0;
          }
          .ft-col-chevron.open { transform: rotate(180deg); }

          .ft-col-body {
            overflow: hidden;
            max-height: 0;
            transition: max-height 0.28s ease, padding 0.22s ease;
            padding-bottom: 0;
          }
          .ft-col-body.open {
            max-height: 300px;
            padding-bottom: 16px;
          }

          /* Desktop heading hidden on mobile (replaced by toggle button) */
          .ft-col-heading { display: none; }

          /* Bottom bar stacks */
          .ft-bottom { flex-direction: column; align-items: flex-start; gap: 14px; }
          .ft-bottom-links { gap: 16px; }
        }
      `}</style>

      <footer className="ft-root">
        <div className="ft-inner">
          <div className="ft-grid">

            {/* ── Brand column ── */}
            <div className="ft-brand">
              <div style={{ marginBottom: "18px" }}>
                <img src={logoImage} alt="California School of Trucking"
                  style={{ height: "141px", width: "auto", objectFit: "contain" }} />
              </div>
              <p style={{ fontSize: "14px", fontWeight: 300, color: "rgba(255,255,255,0.45)", lineHeight: 1.8, maxWidth: "280px", margin: "0 0 22px" }}>
                A federally compliant commercial driver training institution since 1992.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {[
                  { icon: Phone,   text: "+1 (818) 438-3604" },
                  { icon: Mail,    text: "Trucking to you Soon" },
                  { icon: MapPin,  text: "California, United States" },
                  { icon: Package, text: "PO BOX 50713, Pasadena, CA 91115" },
                ].map(({ icon: Icon, text }, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Icon size={13} color={C.gold} />
                    <span style={{ fontSize: "13px", color: "rgba(255,255,255,0.55)" }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Link columns (desktop: static headings / mobile: accordion) ── */}
            {footerColumns.map((col, i) => (
              <FooterColumn key={i} col={col} />
            ))}
          </div>

          {/* ── Bottom bar ── */}
          <div className="ft-bottom">
            <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)" }}>
              &copy; 2026 California School of Trucking. All rights reserved.
            </span>
            <div className="ft-bottom-links">
              {["Privacy Policy", "Terms of Service", "Accessibility"].map((link, i) => (
                <a key={i} href="#" style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)", textDecoration: "none", fontFamily: U }}
                  onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.65)"}
                  onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.3)"}
                >
                  {link}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

// ── Accordion-capable link column ─────────────────────────────────
function FooterColumn({ col }) {
  const [open, setOpen] = useState(false);

  // Need React import — using useState directly
  return (
    <div className="ft-col">
      {/* Desktop static heading */}
      <h5 className="ft-col-heading" style={{
        fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em",
        textTransform: "uppercase", color: C.gold, margin: "0 0 20px",
        fontFamily: U,
      }}>
        {col.title}
      </h5>

      {/* Mobile accordion toggle */}
      <button className="ft-col-toggle" onClick={() => setOpen(o => !o)}>
        {col.title}
        <span className={`ft-col-chevron ${open ? "open" : ""}`}>
          <svg width="12" height="7" viewBox="0 0 12 7" fill="none">
            <path d="M1 1L6 6L11 1" stroke="#A8B8C8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      {/* Links */}
      <div className={`ft-col-body ${open ? "open" : ""}`}>
        {col.links.map((link, j) => (
          <a key={j} href={col.hrefs[j]} style={{
            fontSize: "14px", fontWeight: 300,
            color: "rgba(255,255,255,0.45)", textDecoration: "none",
            transition: "color 0.2s", fontFamily: U,
          }}
            onMouseEnter={e => e.currentTarget.style.color = C.white}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}
          >
            {link}
          </a>
        ))}
      </div>
    </div>
  );
}